import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import YouTubePlayer from 'react-player/youtube';
import styles from './ElementWistiaVideo.module.scss';
import useTranslation from 'next-translate/useTranslation';

export const ElementWistiaVideoPropTypes = {
  videoId: PropTypes.string,
  isYouTube: PropTypes.bool,
  playsInline: PropTypes.bool
};

function ElementWistiaVideo({ videoId, localeVideoIds, isYouTube, playsInline }) {
  const { lang } = useTranslation();
  const [embedHtml, setEmbedHtml] = useState('');
  const [transcriptHtml, setTranscriptHtml] = useState('');
  const options = {
    wistia: {
      videoFoam: true,
      popover: playsInline,
      controls: true
    },
    youtube: {
      playerVars: {
        playsinline: playsInline
      }
    }
  };

  let localeVideoId = ""
  if (localeVideoIds && localeVideoIds?.[lang]) {
    localeVideoId = localeVideoIds[lang]
  }
  else {
    localeVideoId = videoId || localeVideoIds['en-US']
  }

  const [currentPageUrl, setCurrentPageUrl] = useState('');

  useEffect(() => {
    // This code runs only on the client side
    const path = window.location.pathname.split('/').pop();
    setCurrentPageUrl(path);
  }, []);
  useEffect(() => {
    if (isYouTube) return;

    async function loadWistiaEmbeds() {
      try {
        // Player embed
        const playerResponse = await fetch(
          `https://fast.wistia.com/oembed?url=https://dropbox-5.wistia.com/medias/${localeVideoId}`
        );

        const playerData = await playerResponse.json();

        setEmbedHtml(playerData.html);


        // Transcript embed
        const transcriptResponse = await fetch(
          `https://fast.wistia.com/oembed?url=https://dropbox-5.wistia.com/embed/transcripts/${localeVideoId}`
        );

        const transcriptData = await transcriptResponse.json();


        setTranscriptHtml(transcriptData.html);

      } catch (error) {
        console.error('Failed to load Wistia embeds', error);
      }
    }

    loadWistiaEmbeds();

  }, [localeVideoId, isYouTube]);

  useEffect(() => {
    // Function to add data attribute to buttons within the specified div
    const addDataAttributeToButtons = () => {
      const videoWraps = document.querySelectorAll('div[data-testid="ElementWistiaVideoWrap"]');
      if (videoWraps.length > 0) {
        videoWraps.forEach(videoWrap => {
          const buttons = videoWrap.querySelectorAll('button');
          buttons.forEach(button => {
            const ariaLabel = button.getAttribute('aria-label');
            if (ariaLabel) {
              const formattedLabel = ariaLabel
                .replace(/[^a-zA-Z0-9 ]/g, '-')
                .replace(/\s+/g, '-')
                .toLowerCase();
              button.setAttribute('data-uxa-log', `wistiavideo-${formattedLabel}/${currentPageUrl}`);
            } else {
              button.setAttribute('data-uxa-log', `wistiavideo-${currentPageUrl}`);
            }
            button.setAttribute('data-uxa-interactions', `click shown`);
          });
        });
      } else {
        console.warn('No elements with data-testid="ElementWistiaVideoWrap" found.');
      }
    };

    // Call the function once after all elements are loaded
    const handleLoad = () => {
      addDataAttributeToButtons();
      // Remove the event listener after it has been called
      window.removeEventListener('load', handleLoad);
    };

    // Call the function initially
    addDataAttributeToButtons();

    // Observe changes in the DOM or props/state
    const observer = new MutationObserver(() => {
      addDataAttributeToButtons();
    });

    const videoWrap = document.querySelector('div[data-testid="ElementWistiaVideoWrap"]');
    if (videoWrap) {
      observer.observe(videoWrap, { childList: true, subtree: true });
    }

    // Cleanup function to disconnect the observer
    return () => {
      observer.disconnect();
    };
  }, [currentPageUrl]); // Dependency array includes currentPageUrl to run when it changes

  return (
    <div className={styles.iframeWrap} data-testid="ElementWistiaVideoWrap">
      {isYouTube ? (
        <YouTubePlayer
          url={`https://www.youtube.com/watch?v=${localeVideoId}`}
          config={options}
        />
      ) : (
        <div
          className={styles.wistiaEmbed}
          dangerouslySetInnerHTML={{ __html: embedHtml }}
        />
      )}
      {transcriptHtml && (
        <div
          className={styles.transcript}
          dangerouslySetInnerHTML={{
            __html: transcriptHtml,
          }}
        />
      )}
    </div>
  );
}

ElementWistiaVideo.propTypes = ElementWistiaVideoPropTypes;

ElementWistiaVideo.defaultProps = {
  isYouTube: false,
  playsInline: false
};

export default ElementWistiaVideo;